import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, Button, Typography } from '@mui/material';
import { validate as validateUUID } from 'uuid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PATH_TENANTS } from '../../routes/paths';
import apiClient from '../../config/apiClient';

const schema = yup.object().shape({
  id: yup
    .string()
    .required('Tenant ID is required')
    .test('is-uuid', 'Tenant ID must be a valid UUID', (value) => validateUUID(value || '')),
  name: yup.string().required('Tenant name is required'),
  description: yup.string().required('Description is required'),
  threshold: yup.number().required('Threshold is required'),
  orgId: yup.string().required('Org ID is required'),
  applicationId: yup
    .string()
    .required('Application ID is required')
    .test('is-uuid', 'Application ID must be a valid UUID', (value) => validateUUID(value || '')),
});

type FormData = {
  id: string;
  name: string;
  description: string;
  threshold: number;
  orgId: string;
  applicationId: string;
};

const CreateTenantForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const navigate = useNavigate();

  const onSubmit = async (data: FormData) => {
    const tenantData = { ...data };

    try {
      await apiClient.post(`/tenant`, tenantData);

      toast.success('Tenant created successfully! Redirecting in 5 seconds...', {
        position: 'top-right',
      });

      reset();

      setTimeout(() => {
        navigate(PATH_TENANTS.general.view);
      }, 5000);
    } catch (error) {
      toast.error('Error creating tenant. Please try again.', {
        position: 'top-right',
      });

      console.error('Error creating tenant:', error);
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ maxWidth: 400, margin: '0 auto', mt: 5 }}
      >
        <Typography variant="h4" gutterBottom>
          Create Tenant
        </Typography>

        <TextField
          label="Tenant ID"
          fullWidth
          margin="normal"
          {...register('id')}
          error={!!errors.id}
          helperText={errors.id?.message}
        />

        <TextField
          label="Tenant Name"
          fullWidth
          margin="normal"
          {...register('name')}
          error={!!errors.name}
          helperText={errors.name?.message}
        />

        <TextField
          label="Description"
          fullWidth
          margin="normal"
          {...register('description')}
          error={!!errors.description}
          helperText={errors.description?.message}
        />

        <TextField
          label="Threshold"
          fullWidth
          margin="normal"
          type="number"
          {...register('threshold')}
          error={!!errors.threshold}
          helperText={errors.threshold?.message}
        />

        <TextField
          label="Org ID"
          fullWidth
          margin="normal"
          {...register('orgId')}
          error={!!errors.orgId}
          helperText={errors.orgId?.message}
        />

        <TextField
          label="Application ID"
          fullWidth
          margin="normal"
          {...register('applicationId')}
          error={!!errors.applicationId}
          helperText={errors.applicationId?.message}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 3 }}
        >
          Create Tenant
        </Button>
      </Box>
    </>
  );
};

export default CreateTenantForm;
